import { TwoColumnLayout } from '@components/layout';
import { DataTableFacetedFilter } from '@components/table/DataTableFacetedFilter/DataTableFacetedFilter';
import { FormError, FormRow, RadioGroup } from '@components/ui';
import { taskTypeOptions } from '@lib';
import { questionnaireStore } from '@stores';
import { notionStore } from '@stores/notion.store';
import { DataCollectionInputs, DataCollectionOptions } from '@types';
import DOMPurify from 'dompurify';
import { observer } from 'mobx-react';
import ChooseModelStepIllustration from 'public/illustrations/data-collection-step-1.svg';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import styles from './Steps.module.scss';

type ChooseModelStepOptions = Pick<DataCollectionOptions, 'models'>;
export type ChooseModelStepData = Pick<
  DataCollectionInputs,
  'modelId' | 'taskType' | 'modelName' | 'useCaseId' | 'useCaseName'
>;

interface ChooseModelStepProps {
  options: ChooseModelStepOptions;
  onClickNext: (inputs: Partial<ChooseModelStepData>) => void;
}

export const ChooseModelStep = observer(({ options, onClickNext }: ChooseModelStepProps) => {
  const { values } = notionStore;
  const { questionnaireData: data } = questionnaireStore;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ChooseModelStepData>({
    mode: 'onChange',
    defaultValues: { modelId: data?.modelId },
    reValidateMode: 'onChange',
  });

  const onSubmit = handleSubmit(({ modelId }) => {
    const model = options?.models.find(m => m.id === parseInt(modelId.toString(), 10));

    onClickNext({
      taskType: model.taskType,
      modelId: model.id,
      useCaseId: model.useCase.id,
      useCaseName: model.useCase.name,
      modelName: model.name,
    });
  });

  const initialTaskTypeFilter = data?.taskType ? [data.taskType] : [];
  const [selectedTaskTypes, setSelectedTaskTypes] = useState<string[]>(initialTaskTypeFilter);
  const [filteredModels, setFilteredModels] = useState(() => {
    if (initialTaskTypeFilter.length === 0) return options?.models;
    const filtered = options?.models?.filter(m => initialTaskTypeFilter.includes(m.taskType));
    return filtered?.length > 0 ? filtered : [];
  });

  return (
    <TwoColumnLayout
      left={
        options?.models.length !== 0 ? (
          <form
            id="form-id-1"
            onSubmit={onSubmit}
          >
            <FormRow>
              <ol>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(values?.chooseModelStepQuestion1Text) }} />
                <DataTableFacetedFilter
                  title="Model task type"
                  options={taskTypeOptions}
                  list={options.models}
                  fieldName="taskType"
                  values={selectedTaskTypes}
                  onSelect={selectedValues => {
                    setSelectedTaskTypes(selectedValues);
                    const filtered = selectedValues
                      ? options.models.filter(model => selectedValues?.includes(model.taskType))
                      : undefined;
                    if (filtered?.length > 0) {
                      setFilteredModels(filtered);
                    } else if (!selectedValues && !filtered) {
                      setFilteredModels(options.models);
                    } else setFilteredModels([]);
                  }}
                  className={styles.filterBtn}
                  classNameContent={styles.taskTypeFilter}
                />
                <br />
                <RadioGroup
                  className={styles.modelRadio}
                  options={filteredModels}
                  inputProps={{
                    ...register('modelId', { required: 'Please select model' }),
                    defaultValue: data?.modelId,
                  }}
                />
              </ol>
              <FormError error={errors.modelId?.message} />
            </FormRow>
          </form>
        ) : (
          <div className={styles.emptyInfo}>
            There are no added models, please add models in order to make assessments.
          </div>
        )
      }
      right={<ChooseModelStepIllustration />}
    />
  );
});
